import styled from "styled-components";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Icon, Input } from "semantic-ui-react";
import Filters from "./Filters";
import QuestionDropdown from "./QuestionDropdown";
import RadioButtonGroup from "./components/RadioButton";
import HorizontalBarChart from "components/Charts/HorizontalBarChart";
import { convertFeedbackData } from "components/Charts/barChartUtils";
import { usePageTranslations } from "hooks/usePageTranslations";
import { useLanguage } from "hooks/useLanguage";

// These will be translated dynamically in the component
const getChartOptions = (t) => [
  { name: t('veryNegative'), color: "#de2121", key: "Very Negative" },
  { name: t('slightlyNegative'), color: "#F57373", key: "Slightly Negative" },
  { name: t('neutral'), color: "#F7B531", key: "Neutral" },
  { name: t('slightlyPositive'), color: "#80C7A0", key: "Slightly Positive" },
  { name: t('veryPositive'), color: "#3D8B67", key: "Very Positive" },
];

const getSentiments = (t) => [
  { id: "Very Negative", value: "Very Negative", name: t('veryNegative') },
  {
    id: "Slightly Negative",
    value: "Slightly Negative",
    name: t('slightlyNegative'),
  },
  { id: "Neutral", value: "Neutral", name: t('neutral') },
  {
    id: "Slightly Positive",
    value: "Slightly Positive",
    name: t('slightlyPositive'),
  },
  { id: "Very Positive", value: "Very Positive", name: t('veryPositive') },
];

const FeedbackTag = ({
  data = [],
  outcomeQ,
  isOTP,
  selectedQuestion,
  setQuestion,
  showComments,
  feedbackType,
  setFeedbackType,

  // Multi-select sentiment handling:
  selectedSentiments = [],
  setSelectedSentiments,
  displayedFeedback,
  setSelectedOutcomes,
  
  // Search functionality
  search,
  setSearch,
  setActivePage,
  showEmptyPage,
}) => {
  const t = usePageTranslations('app/audit/simple/FeedbackTag/translations');
  const { language } = useLanguage();

  const [chartData, setChartData] = useState([]);
  const [immutableChartData, setImmutableChartData] = useState([]);
  const [actionablePercentages, setActionablePercentages] = useState([]);
  const [loading, setLoading] = useState(false);

  const { core_data } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
    }),
    shallowEqual
  );

  // For comment questions if needed
  const [commentQuestions, setCommentQuestions] = useState([]);
  const originalDataRef = useRef();

  useEffect(() => {
    if (core_data?.questions?.comments?.length > 0 && !isOTP) {
      // If we aren't OTP use the commentQuestions found in the core_data?.questions?.comments
      const allOption = {
        id: null,
        value: null,
        name: t('allComments'),
      };

      const questionOptions = core_data?.questions?.comments?.map((q) => {
        return {
          id: q.id,
          value: q.id,
          name: q.question,
        };
      });

      setCommentQuestions([allOption, ...questionOptions]);
    }
  }, [core_data]);

  // Convert your data to chart data
  useEffect(() => {
    setLoading(true);

    // Summarize sentiments
    const sentimentData = {};
    data.forEach((item) => {
      const s = item?.nlp?.sentiment;
      const isAction = item?.nlp?.action_orientation === "action_oriented";
      if (s) {
        if (!sentimentData[s]) {
          sentimentData[s] = { total: 0, action_oriented: 0 };
        }
        sentimentData[s].total++;
        if (isAction) sentimentData[s].action_oriented++;
      }
    });

    // Keep consistent order (using English keys for data matching)
    const sentimentOrder = [
      "Very Negative",
      "Slightly Negative",
      "Neutral",
      "Slightly Positive",
      "Very Positive",
    ];

    // Get translated chart options
    const translatedChartOptions = getChartOptions(t);

    // Build series with translated names
    const _series = sentimentOrder.map((s) => {
      const total = sentimentData[s]?.total || 0;
      // Find the translated name for this sentiment
      const translatedOption = translatedChartOptions.find(opt => opt.key === s);
      const displayName = translatedOption ? translatedOption.name : s;
      return { name: displayName, data: [total], key: s }; // Keep key for filtering
    });

    // Calculate action-oriented percentages
    const _actionable = sentimentOrder.map((s) => {
      const total = sentimentData[s]?.total || 0;
      const actionOriented = sentimentData[s]?.action_oriented || 0;
      return total > 0 ? ((actionOriented / total) * 100).toFixed(1) : "0.0";
    });

    // Convert to chart friendly (using translated chart options)
    const cData = convertFeedbackData(_series, translatedChartOptions, _actionable);
    // Add key field to each item for filtering (English key for selectedSentiments matching)
    const cDataWithKeys = cData.map((item, index) => ({
      ...item,
      key: sentimentOrder[index], // Add English key for filtering
    }));
    setChartData(cDataWithKeys);
    setImmutableChartData(cDataWithKeys);
    setActionablePercentages(_actionable);

    setLoading(false);
  }, [data, t]);

  // Example of your overall feedback percentage usage
  const getFeedbackPercentage = () => {
    let total = 0;
    let action = 0;
    data.forEach((item) => {
      if (item?.nlp?.action_orientation === "action_oriented") action++;
      total++;
    });
    return total === 0 ? 0 : (action / total) * 100;
  };

  // Filter config for your "Filters" component
  const feedbackFiltersConfig = [
    {
      id: "outcome",
      title: t('outcomes'),
      data: outcomeQ?.questions,
      callback: setSelectedOutcomes,
    },
    {
      id: "sentiment",
      title: t('sentiment'),
      data: getSentiments(t),
      // Instead of setting one "sentiment",
      // we pass an array of sentiments back to the parent
      callback: (selectedArray) => {
        setSelectedSentiments(selectedArray);
      },
    },
  ];

  // Handle user toggling a bar from the chart
  const handleBarSelect = (sentimentKey) => {
  // sentimentKey is now the English key passed from the chart
    // Check if it's already selected
    if (selectedSentiments.includes(sentimentKey)) {
      // remove it
      const newArray = selectedSentiments.filter((s) => s !== sentimentKey);
      setSelectedSentiments(newArray);
    } else {
      // add it (using English key for filtering)
      setSelectedSentiments([...selectedSentiments, sentimentKey]);
    }
  };

  // Determine if we should show tabs - use existing logic
  // Show tabs if: not OTP, has comments available, and has actual feedback data
  const shouldShowTabs = !isOTP && showComments && data?.length > 0;

  return (
    <Container hasFeedback={displayedFeedback?.length > 0}>
      {shouldShowTabs && (
        <TabContainer>
          <Tab 
            active={feedbackType === 0} 
            onClick={() => setFeedbackType(0)}
          >
            {t('feedback')}
          </Tab>
          <Tab 
            active={feedbackType === 1} 
            onClick={() => setFeedbackType(1)}
          >
            {t('comments')}
          </Tab>
        </TabContainer>
      )}

      {/* Sentiment Distribution Chart */}
      {!loading &&
        immutableChartData?.length > 0 &&
        ((displayedFeedback?.length > 0 && selectedSentiments?.length === 0) ||
          selectedSentiments?.length > 0) && (
          <ChartContainer>
          <ChartTitle>{t('sentimentDistribution')}</ChartTitle>
            <HorizontalBarChart
              data={immutableChartData}
              actionablePercentages={actionablePercentages}
              selectedSentiments={selectedSentiments}
            onSelect={(barName, index) => {
              // Use the key from the data item for filtering
              const item = immutableChartData[index];
              const sentimentKey = item?.key || barName;
              handleBarSelect(sentimentKey);
            }}
            />
          </ChartContainer>
        )}

      <DropdownSection>
        {(isOTP || feedbackType === 1) && (
          <QuestionDropdownContainer>
            <QuestionDropdown
              label={t('filterByQuestion')}
              selectedOption={selectedQuestion}
              setSelected={(val) => setQuestion(val)}
              options={commentQuestions}
            />
          </QuestionDropdownContainer>
        )}
        <SearchContainer isFullWidth={!(isOTP || feedbackType === 1)}>
          <SearchLabel>{t('search')}</SearchLabel>
          <StyledInput
            icon={{ name: "search" }}
            placeholder={t('searchByKeyword')}
            value={search}
            onChange={(e) => {
              setActivePage(1);
              setSearch(e.target.value);
            }}
          />
        </SearchContainer>
      </DropdownSection>

      <FiltersSection>
        <Filters
          hideFactors={false}
          filters={feedbackFiltersConfig}
          outcomes={outcomeQ?.questions}
          onSelectOutcomes={setSelectedOutcomes}
          barSentiments={selectedSentiments}
          onSentimentFilter={(selected) => setSelectedSentiments(selected)}
          // If your filter component can handle multi-check for sentiments,
          // it should call `callback([arrayOfSelected])`.
        />
      </FiltersSection>
    </Container>
  );
};

export default FeedbackTag;

/* --- Styles simplified --- */

const Container = styled.div`
  margin-bottom: ${({ hasFeedback }) => (hasFeedback ? "16px" : "0")};
  position: relative;
  background: transparent;
`;

const FiltersSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
`;

const ChartContainer = styled.div`
  margin: 16px 0;
  width: 100%;
  background: white;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  padding: 16px;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1d29;
  margin: 0 0 16px 0;
  line-height: 1.3;
`;

const DropdownSection = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 16px 0;
`;

const SearchContainer = styled.div`
  flex: ${({ isFullWidth }) => isFullWidth ? '1' : '0 0 30%'};
`;

const QuestionDropdownContainer = styled.div`
  flex: 0 0 70%;
  min-width: 0;
  position: relative;
`;

const SearchLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  &&& {
    width: 100%;
    border-radius: 8px;
    background-color: white;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    input {
      border: none;
      background: none;
      font-size: 14px;
      color: #374151;
      padding: 12px 16px;
      &::placeholder {
        color: #9ca3af;
      }
    }

    .icon {
      margin-right: 16px;
      color: #6b7280;
    }

    &:focus-within {
      border-color: #2d70e2;
      box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
    }
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ active }) => active ? '#2d70e2' : '#6b7280'};
  border-bottom: 2px solid ${({ active }) => active ? '#2d70e2' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ active }) => active ? '#2d70e2' : '#374151'};
    background-color: ${({ active }) => active ? 'transparent' : '#f9fafb'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(45, 112, 226, 0.1);
  }
`;
